import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Factory,
  Mountains,
  Buildings,
  ShoppingBag,
  Broadcast,
  ChartBar,
} from "@phosphor-icons/react";

const platforms = [
  {
    id: "manufacturing",
    name: "AI Manufacturing Suite",
    icon: Factory,
    tagline: "Smart factories, quantified.",
    body: "End-to-end factory intelligence: quality prediction, OEE analytics, digital twins, and connected shop-floor operations.",
    features: [
      "Quality prediction & vision analytics",
      "OEE + throughput dashboards",
      "Digital twin of production line",
      "Predictive maintenance workflows",
    ],
    tint: "bg-blue-50",
    accent: "bg-blue-600",
    text: "text-blue-700",
  },
  {
    id: "mining",
    name: "AI Mining Platform",
    icon: Mountains,
    tagline: "Digital mines. Real safety.",
    body: "Fleet optimization, ore quality prediction, and safety intelligence tailored to open-pit and underground operations.",
    features: [
      "Fleet & haul-cycle optimization",
      "Ore grade prediction",
      "Safety & incident analytics",
      "Digital mine control room",
    ],
    tint: "bg-amber-50",
    accent: "bg-amber-500",
    text: "text-amber-700",
  },
  {
    id: "facility",
    name: "Smart Facility Platform",
    icon: Buildings,
    tagline: "Buildings that think.",
    body: "Unified facility operations across tenants, energy, access, and parking — with intelligent automation baked in.",
    features: [
      "Energy optimization",
      "Tenant management",
      "Parking & access control",
      "Smart maintenance automation",
    ],
    tint: "bg-teal-50",
    accent: "bg-teal-600",
    text: "text-teal-700",
  },
  {
    id: "procurement",
    name: "AI Procurement Portal",
    icon: ShoppingBag,
    tagline: "Sourcing at machine speed.",
    body: "AI-first procurement platform for vendor discovery, contract intelligence, and category-level spend optimization.",
    features: [
      "Vendor & catalog intelligence",
      "Contract analytics",
      "Category spend optimization",
      "Automated RFQ workflows",
    ],
    tint: "bg-emerald-50",
    accent: "bg-emerald-600",
    text: "text-emerald-700",
  },
  {
    id: "command",
    name: "AI Command Center",
    icon: Broadcast,
    tagline: "Unified 24×7 operations.",
    body: "A single pane of glass for the intelligent enterprise — unified monitoring, decision support, and autonomous alerts.",
    features: [
      "Unified operations monitoring",
      "Autonomous decision support",
      "Root-cause analysis",
      "Cross-plant intelligence",
    ],
    tint: "bg-rose-50",
    accent: "bg-rose-500",
    text: "text-rose-700",
    featured: true,
  },
  {
    id: "analytics",
    name: "Intelligent Analytics Platform",
    icon: ChartBar,
    tagline: "From data to decisions.",
    body: "Cloud-native analytics stack with data lakes, ESG intelligence, and generative-AI-powered insight for leadership.",
    features: [
      "Modern data platform",
      "ESG analytics",
      "Generative AI copilots",
      "Executive intelligence dashboards",
    ],
    tint: "bg-violet-50",
    accent: "bg-violet-600",
    text: "text-violet-700",
  },
];

export default function Platforms() {
  return (
    <div data-testid="page-platforms">
      {/* HERO */}
      <section className="border-b border-zinc-200" data-testid="platforms-hero">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="text-overline mb-6">Featured Platforms</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            From architecture to actionable suites.
          </h1>
          <p className="mt-8 text-xl leading-relaxed text-zinc-600 max-w-3xl">
            Featured platforms engineered for rapid implementation and time-to-value — modular,
            cloud-native, and industry-tuned.
          </p>
        </div>
      </section>

      {/* PLATFORMS GRID */}
      <section className="border-b border-zinc-200" data-testid="platforms-grid-section">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {platforms.map((p, i) => (
              <article
                key={p.id}
                data-testid={`platform-card-${p.id}`}
                className={`${p.tint} border border-zinc-200 p-8 lg:p-12 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group flex flex-col relative overflow-hidden`}
              >
                <div className={`absolute top-0 left-0 h-2 w-full ${p.accent}`} />
                {p.featured && (
                  <div className="absolute top-4 right-4 bg-zinc-950 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1">
                    ★ Flagship
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${p.accent} text-white shadow-md`}>
                    <p.icon size={30} weight="light" />
                  </div>
                  <div className="text-right">
                    <div className={`text-overline ${p.text}`}>Suite</div>
                    <div className={`text-3xl font-black ${p.text}`} style={{ fontFamily: "Chivo" }}>0{i + 1}</div>
                  </div>
                </div>

                <h3 className="mt-8 text-2xl lg:text-3xl font-black tracking-tighter">{p.name}</h3>
                <p className={`mt-2 text-lg font-semibold ${p.text}`} style={{ fontFamily: "Chivo" }}>
                  {p.tagline}
                </p>
                <p className="mt-4 text-zinc-700 leading-relaxed">{p.body}</p>

                <ul className="mt-6 grid grid-cols-1 gap-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-900 bg-white/80 px-3 py-2 border border-white">
                      <span className={`mt-2 w-2 h-2 ${p.accent} shrink-0 rounded-full`} />
                      <span className="font-medium">{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`platform-request-demo-${p.id}`}
                  className={`mt-8 self-start inline-flex items-center gap-2 ${p.accent} text-white px-5 py-3 text-sm font-semibold hover:opacity-90 transition-opacity shadow-md`}
                >
                  Request a Demo <ArrowUpRight size={14} weight="bold" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-testid="platforms-cta">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <h2 className="lg:col-span-8 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
              A platform for every operation. An engine for every outcome.
            </h2>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/contact"
                data-testid="platforms-cta-btn"
                className="inline-flex items-center justify-between bg-zinc-950 text-white px-6 py-4 text-sm font-semibold hover:bg-[#0A33D8] transition-colors"
              >
                Book a Consultation <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link
                to="/architecture"
                data-testid="platforms-architecture-btn"
                className="inline-flex items-center justify-between border border-zinc-950 px-6 py-4 text-sm font-semibold hover:bg-zinc-950 hover:text-white transition-colors"
              >
                See the Architecture <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
